import type { WorkIssue } from '@/types/github'

// GitHub search can't filter on custom Issue Fields (verified: `stage:X`
// matches body text, not the field), so the app fetches the open-issue
// universe and evaluates these GitHub-search-shaped filter strings locally.
// Syntax kept compatible with the saved-search catalogue in n3oltd/work:
//   is:open stage:Backlog label:a,b -stage:Done sprint:@current "free text"

export type FilterTerm = {
  key: string
  /** Comma-separated values are OR within the term; multiple terms AND. */
  values: string[]
  negated: boolean
}

export type ParsedFilter = {
  terms: FilterTerm[]
  /** Free text — substring match on title or #number. */
  text: string
}

const KNOWN_KEYS = new Set([
  'is',
  'state',
  'stage',
  'priority',
  'sprint',
  'quarter',
  'effort',
  'product',
  'workload',
  'client',
  'source',
  'verifier',
  'label',
  'type',
  'assignee',
  'author',
])

function tokenize(input: string): string[] {
  // A token is either `prefix"quoted remainder"` (covers stage:"In progress"
  // and bare "quoted text") or a plain non-space run.
  return input.match(/[^\s"]*"[^"]*"|[^\s"]+/g) ?? []
}

export function parseFilter(input: string): ParsedFilter {
  const terms: FilterTerm[] = []
  const textParts: string[] = []

  for (const token of tokenize(input)) {
    const negated = token.startsWith('-')
    const body = negated ? token.slice(1) : token
    const colon = body.indexOf(':')
    const key = colon > 0 ? body.slice(0, colon).toLowerCase() : ''

    if (colon > 0 && KNOWN_KEYS.has(key)) {
      const raw = body.slice(colon + 1).replace(/^"|"$/g, '')
      const values = raw.split(',').map((v) => v.trim()).filter(Boolean)
      if (values.length > 0) terms.push({ key: key === 'state' ? 'is' : key, values, negated })
    } else if (token.trim()) {
      textParts.push(token.replace(/^"|"$/g, ''))
    }
  }

  return { terms, text: textParts.join(' ').trim() }
}

/** Inverse of parseFilter: render terms + free text back to a filter string.
 *  Values containing spaces are quoted so they re-parse as one token.
 *  Caveat: a multi-value term where a value contains a space (e.g.
 *  `label:"a b",c`) does NOT round-trip — the tokenizer splits the comma out.
 *  The FacetBar only ever emits single-value terms, so it never hits this. */
export function serializeFilter(filter: ParsedFilter): string {
  const quote = (v: string) => (/\s/.test(v) ? `"${v}"` : v)
  const parts = filter.terms.map(
    (t) => `${t.negated ? '-' : ''}${t.key}:${t.values.map(quote).join(',')}`,
  )
  if (filter.text) parts.push(quote(filter.text))
  return parts.join(' ')
}

export type FilterContext = {
  /** Resolution for `sprint:@current`; null means "no active sprint". */
  currentSprintName?: string | null
  /** Resolution for `assignee:@me` / `author:@me`. */
  viewerLogin?: string
}

function issueValues(issue: WorkIssue, key: string): string[] {
  switch (key) {
    case 'is':
      return [issue.state === 'OPEN' ? 'open' : 'closed']
    case 'stage':
      return issue.stage ? [issue.stage] : []
    case 'priority':
      return issue.priority ? [issue.priority] : []
    case 'sprint':
      return issue.sprint ? [issue.sprint] : []
    case 'quarter':
      return issue.quarter ? [issue.quarter] : []
    case 'effort':
      return issue.effort ? [issue.effort] : []
    case 'product':
      return issue.product ? [issue.product] : []
    case 'workload':
      return issue.workload ? [issue.workload] : []
    case 'client':
      return issue.client ? [issue.client] : []
    case 'source':
      return issue.source ? [issue.source] : []
    case 'verifier':
      return issue.verifier ? [issue.verifier] : []
    case 'label':
      return issue.labels
    case 'type':
      return issue.typeName ? [issue.typeName] : []
    case 'assignee':
      return issue.assignees.map((a) => a.login)
    case 'author':
      return issue.author ? [issue.author] : []
    default:
      return []
  }
}

function termMatches(issue: WorkIssue, term: FilterTerm, ctx: FilterContext): boolean {
  const haystack = issueValues(issue, term.key).map((v) => v.toLowerCase())
  return term.values.some((wanted) => {
    let resolved = wanted
    if (term.key === 'sprint' && wanted === '@current') resolved = ctx.currentSprintName ?? ''
    if (
      (term.key === 'assignee' || term.key === 'author' || term.key === 'verifier') &&
      wanted === '@me'
    ) {
      resolved = ctx.viewerLogin ?? ''
    }
    return resolved !== '' && haystack.includes(resolved.toLowerCase())
  })
}

export function matchesFilter(issue: WorkIssue, filter: ParsedFilter, ctx: FilterContext = {}): boolean {
  for (const term of filter.terms) {
    const matched = termMatches(issue, term, ctx)
    if (term.negated ? matched : !matched) return false
  }

  if (filter.text) {
    const needle = filter.text.toLowerCase()
    const numberForm = needle.startsWith('#') ? needle.slice(1) : needle
    if (
      !issue.title.toLowerCase().includes(needle) &&
      String(issue.number) !== numberForm
    ) {
      return false
    }
  }

  return true
}

export function filterIssues(
  issues: WorkIssue[],
  filter: ParsedFilter,
  ctx: FilterContext = {},
): WorkIssue[] {
  return issues.filter((issue) => matchesFilter(issue, filter, ctx))
}
